import { useCallback, useMemo } from "react";
import { Dp } from "./CoState";
import { Filter } from "./CoQuery";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import { IconButton } from "@mui/material";
import OpenInFull from "@mui/icons-material/OpenInFull";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import { use as useSet } from "common/src/data/TftSet.bs";

const portalLabels = [
  "Other",
  "TFT_Portals_Headliner_Superstar",
  "TFT_Portals_Combat_TrainingDummy",
  "TFT_Portals_Items_ArtifactAnvil",
  "TFT_Portals_Items_RadiantItem",
  "TFT_Portals_Unique_Spatula",
  "TFT_Portals_Items_TacticiansCrown",
  "TFT_Portals_Items_SupportAnvil",
  "TFT_Portals_Combat_WanderingTrainers",
];

const cols = [
  makeCol({
    width: 180,
    order: ([unitId], _, { unitsT }) => unitsT(unitId),
    defaultDesc: false,
    renderTitle: (_, { t }) => <> {t("Portals")} </>,
    renderValue: ([portalId, _1], { onExpand }, { t }) => (
      <div
        className="flex items-center text-sm cursor-pointer"
        // onClick={(_) =>
        //   onExpand({
        //     typ: "u",
        //     id: unitId,
        //     sid: unitId,
        //     starLevel: 0,
        //     label: unitsT(unitId),
        //   })
        // }
      >
        {/* <div className="flex-shrink-0">
          <UnitTooltip unit={unitId}>
            <UnitImage unitId={unitId} size={28} />
          </UnitTooltip>
        </div> */}
        <div className="pl-[6px] font-roboto font-normal truncate">
          {t(portalLabels[portalId])}
        </div>
      </div>
    ),
  }),
  makeCol({
    width: 90,
    order: ([_, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Games")} </>,
    renderValue: ([_, v]) => <> {humanReadableCount(v.count)} </>,
  }),
  makeCol({
    width: 90,
    order: ([_, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Play rate")} </>,
    tooltip: ([_, v]) => <> {humanReadableCount(v.count) + " games"} </>,
    renderValue: ([_, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: ([_, v]) => v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Place")} </>,
    renderValue: ([_, v]) => <> {(v.place / v.count).toFixed(2)} </>,
  }),
  makeCol({
    width: 76,
    order: ([_, v]) => v.delta,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => (
      <Tooltip
        title={
          "Delta shows the difference in average placement of the selected filter combination with the unit compared to the average placement of the filter combination without the unit. Lower is better."
        }
      >
        <div>{t("Delta")}</div>
      </Tooltip>
    ),

    renderValue: ([_, v]) => {
      return v.delta ? (
        <> {(v.delta > 0 ? "+" : "") + v.delta.toFixed(2)} </>
      ) : (
        <>—</>
      );
    },
  }),
  makeCol({
    width: 90,
    order: ([_, v]) => v.top4 / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Top 4")}% </>,
    renderValue: ([_, v]) => <> {formatPct2(v.top4 / v.count)} </>,
  }),
  makeCol({
    width: 80,
    order: ([_, v]) => v.won / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Win")}% </>,
    renderValue: ([_, v]) => <> {formatPct2(v.won / v.count)} </>,
  }),
  makeCol({
    width: 48,
    alignRight: true,
    renderTitle: (_) => <div className="h-1" />,
    valueClass: "!justify-start",
    renderValue: (_, { onExpand, hideExpand }, { unitsT }) => {
      return true || hideExpand ? (
        <div />
      ) : (
        <>
          <IconButton
            onClick={(_) =>
              // onExpand({
              //   typ: "u",
              //   id: unitId,
              //   sid: unitId,
              //   starLevel: 0,
              //   label: unitsT(unitId),
              // })
              {}
            }
            classes={{
              root: "text-lg",
            }}
          >
            <OpenInFull fontSize="inherit" />
          </IconButton>
        </>
      );
    },
  }),
];

type Props = {
  data: Array<[number, Dp]>;
  baseCount: number;
  onExpand: (f: Filter) => void;
  hideExpand: boolean;
  search: string;
  minSample: number;
};

const CoPortals = ({
  data,
  baseCount,
  onExpand,
  hideExpand,
  search,
  minSample,
}: Props) => {
  const set = useSet();
  const keyFn = useCallback(([id, _]: [string, Dp]) => id, []);
  const context = useMemo(
    () => ({ baseCount, onExpand, hideExpand }),
    [baseCount, onExpand, hideExpand],
  );

  const visibleFilter = useMemo(() => {
    if (search === "") {
      return () => true;
    }
    // const passIds = searchUnits(search, set);

    // return ([unitId]: [string]) => passIds.has(unitId);
    return () => true;
  }, [search]);

  const filteredData = useMemo(
    () => data.filter(([_, v]) => v.count >= minSample),
    [data, minSample],
  );

  const hasResults = true;
  // let hasResults =
  //   search === "" || filteredData.some((v) => visibleFilter([v[0]]));

  return filteredData.length === 0 ? (
    <div className="px-5 py-6 text-xl text-medium text-white2">
      No unit combos with at least {minSample} games played
    </div>
  ) : (
    <>
      <StatsTable
        cols={cols}
        data={filteredData}
        keyFn={keyFn}
        stickyColCount={1}
        defaultSortIdx={1}
        top="t262"
        context={context}
        visibleFilter={visibleFilter}
      />
      {hasResults ? null : (
        <div className="p-5 text-xl text-medium text-white2">
          No results found matching the search
        </div>
      )}
    </>
  );
};

export default CoPortals;
